/**
 * Active Buyer Report - API response adapter.
 *
 * The Stream A backend returns a flat data shape (counts, percentages,
 * raw rows). The Stream B view layer was built against a richer
 * fixture shape with display strings, copy, and chart-ready geometry.
 *
 * adaptApiResponse() bridges the two. It is idempotent - if the input
 * already looks like the view shape (presence of `apiData.hero`) it is
 * returned untouched, so existing fixture loads and dev fallbacks keep
 * working.
 */

const ACCENT_COLOR = "#2D5F73";
const INK_COLOR = "rgba(42,47,59,1.0)";
const DIM_COLOR = "rgba(42,47,59,0.40)";
const FAINT_COLOR = "rgba(42,47,59,0.22)";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const TIMEZONE_SUFFIX_BY_STATE = {
  AZ: "MT",
  CA: "PT", OR: "PT", WA: "PT", NV: "PT",
  TX: "CT", IL: "CT", MO: "CT", OK: "CT", LA: "CT", AR: "CT", MN: "CT", IA: "CT", WI: "CT", MS: "CT", AL: "CT", TN: "CT", KS: "CT", NE: "CT", ND: "CT", SD: "CT",
  IN: "ET", OH: "ET", MI: "ET", KY: "ET", PA: "ET", NY: "ET", NJ: "ET", VA: "ET", WV: "ET", MD: "ET", DE: "ET", DC: "ET", NC: "ET", SC: "ET", GA: "ET", FL: "ET", MA: "ET", CT: "ET", RI: "ET", NH: "ET", VT: "ET", ME: "ET",
  CO: "MT", UT: "MT", NM: "MT", WY: "MT", MT: "MT", ID: "MT",
  HI: "HT", AK: "AKT"
};

function safeNum(n, fallback = 0) {
  if (n === null || n === undefined) return fallback;
  const parsed = Number(n);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function formatPrice(n) {
  const num = safeNum(n, 0);
  if (!num) return "$0";
  return `$${num.toLocaleString()}`;
}

export function formatK(n) {
  const num = safeNum(n, 0);
  if (!num) return "$0";
  if (num >= 1000000) {
    const m = num / 1000000;
    return Number.isInteger(m) ? `${m}M` : `${m.toFixed(1)}M`;
  }
  const k = Math.round(num / 1000);
  return `${k}K`;
}

export function formatDateTime(iso, state) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const month = MONTH_NAMES[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const mm = String(minutes).padStart(2, "0");
  const tz = TIMEZONE_SUFFIX_BY_STATE[state] || "PT";
  return `${month} ${day}, ${year} - ${hours}:${mm} ${ampm} ${tz}`;
}

export function slugify(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
}

export function parseAddress(full) {
  if (!full || typeof full !== "string") {
    return { street: "", city: "", state: "", zip: "" };
  }
  const parts = full.split(",").map((p) => p.trim()).filter(Boolean);
  const street = parts[0] || "";
  let city = "";
  let state = "";
  let zip = "";
  if (parts.length >= 3) {
    city = parts[1] || "";
    const tail = parts[parts.length - 1] || "";
    const zipMatch = tail.match(/\b(\d{5}(?:-\d{4})?)\b/);
    if (zipMatch) zip = zipMatch[1];
    const stateMatch = (parts[2] || tail).match(/\b([A-Z]{2})\b/);
    if (stateMatch) state = stateMatch[1];
  } else if (parts.length === 2) {
    const tail = parts[1];
    const tailMatch = tail.match(/^([A-Za-z .]+?)\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (tailMatch) {
      city = tailMatch[1];
      state = tailMatch[2];
      zip = tailMatch[3];
    } else {
      city = tail;
    }
  }
  return { street, city, state, zip };
}

function pickPrimaryFinance(financeMix) {
  if (!financeMix || typeof financeMix !== "object") return null;
  let topKey = null;
  let topVal = -1;
  Object.entries(financeMix).forEach(([k, v]) => {
    const n = safeNum(v);
    if (n > topVal) {
      topVal = n;
      topKey = k;
    }
  });
  return topKey;
}

function formatBudgetRange(min, max) {
  if (!min && !max) return "";
  if (min && max) return `${formatK(min) ? "$" + formatK(min) : ""} - ${formatK(max) ? "$" + formatK(max) : ""}`.replace(/\$\$/g, "$");
  return min ? `$${formatK(min)}+` : `Up to $${formatK(max)}`;
}

function formatBudgetRangeClean(min, max) {
  const lo = min ? `$${formatK(min)}` : "";
  const hi = max ? `$${formatK(max)}` : "";
  if (lo && hi) return `${lo} - ${hi}`;
  return lo || hi || "";
}

function buildBudgetChart(budgetDistribution, askPrice) {
  if (!budgetDistribution) {
    return { askDisplay: formatPrice(askPrice), axis: [], bars: [], caption: "" };
  }
  const bins = Array.isArray(budgetDistribution.bins) ? budgetDistribution.bins : [];
  const sweetMin = budgetDistribution.sweetSpotMin;
  const sweetMax = budgetDistribution.sweetSpotMax;
  const ask = askPrice || budgetDistribution.askPrice || 0;

  const maxCount = bins.reduce((m, b) => Math.max(m, safeNum(b.count)), 0) || 1;
  const bars = bins.map((b, idx) => {
    const height = Math.max(6, Math.round((safeNum(b.count) / maxCount) * 60));
    const rangeMatch = String(b.range || "").match(/\$(\d+)-(\d+)K/);
    let accent = false;
    if (rangeMatch && sweetMin && sweetMax) {
      const lo = Number(rangeMatch[1]) * 1000;
      const hi = Number(rangeMatch[2]) * 1000;
      accent = lo >= sweetMin - 25000 && hi <= sweetMax + 25000;
    }
    return { x: idx * 44, height, accent };
  });

  let axis = [];
  if (bins.length) {
    const lows = bins.map((b) => {
      const m = String(b.range).match(/\$(\d+)/);
      return m ? Number(m[1]) : 0;
    });
    const lo = Math.min(...lows);
    const hi = Math.max(...lows) + 25;
    const step = Math.max(25, Math.round((hi - lo) / 3));
    axis = [`$${lo}K`, `$${lo + step}K`, `$${lo + step * 2}K`, `$${hi + 25}K`];
  }

  const sweetCount = bins.reduce((acc, b, idx) => bars[idx]?.accent ? acc + safeNum(b.count) : acc, 0);
  const totalCount = bins.reduce((acc, b) => acc + safeNum(b.count), 0);
  const pct = totalCount ? Math.round((sweetCount / totalCount) * 100) : 0;
  const sweetLabel = sweetMin && sweetMax ? `$${formatK(sweetMin)}-$${formatK(sweetMax)}` : "the sweet spot";
  const askInside = ask >= (sweetMin || 0) && ask <= (sweetMax || Infinity);
  const askNote = askInside ? "sits inside the heaviest cluster" : "sits outside the heaviest cluster";

  return {
    askDisplay: `$${formatK(ask)}`,
    axis,
    bars,
    caption: `**${pct}% of matches fall between ${sweetLabel}**. Your $${formatK(ask)} ask ${askNote}.`
  };
}

function buildTimingForView(timelineDistribution) {
  const fallback = { buckets: [], caption: "" };
  if (!timelineDistribution) return fallback;
  const readyNow = safeNum(timelineDistribution.ready_now);
  const d30 = safeNum(timelineDistribution["30_days"]);
  const d60 = safeNum(timelineDistribution["60_days"]);
  const d90 = safeNum(timelineDistribution["90_plus"]);

  const buckets = [
    { label: "< 30d", pct: readyNow, accent: readyNow >= 25 },
    { label: "30-60d", pct: d30, accent: d30 >= 25 },
    { label: "60-90d", pct: d60 },
    { label: "90d+", pct: d90 }
  ];
  const within60 = readyNow + d30;
  const caption = `**${within60}% of matches are ready within 60 days**; ${readyNow}% can go under contract in 30.`;
  return { buckets, caption };
}

function buildFinanceForView(financeMix) {
  const fallback = { headlineRight: "", segments: [] };
  if (!financeMix) return fallback;
  const conv = safeNum(financeMix.Conventional);
  const cash = safeNum(financeMix.Cash);
  const fha = safeNum(financeMix.FHA);
  const va = safeNum(financeMix.VA);
  const other = safeNum(financeMix.Other);
  const vaOther = va + other;

  const segments = [
    { label: "Conventional, pre-approved", pct: conv, color: INK_COLOR, tone: "ink" },
    { label: "Cash", pct: cash, color: ACCENT_COLOR, tone: "accent" },
    { label: "FHA", pct: fha, color: DIM_COLOR, tone: "dim" },
    { label: "VA / Other", pct: vaOther, color: FAINT_COLOR, tone: "dim" }
  ].filter((s) => s.pct > 0);

  const convCash = conv + cash;
  return {
    headlineRight: `Conventional + cash = **${convCash}% of matches**`,
    segments
  };
}

function buildPrioritiesRanked(matchPriorities) {
  if (!matchPriorities || !Array.isArray(matchPriorities.topNiceToHaves)) return [];
  return matchPriorities.topNiceToHaves.map((t) => {
    let kind;
    if (t.propertyHas) kind = "met";
    else if (t.isTopMissing) kind = "gap-top";
    else kind = "gap";
    const item = {
      label: t.name,
      pct: safeNum(t.percent),
      kind
    };
    if (kind === "gap-top") item.note = "Top missing nice-to-have";
    return item;
  });
}

function buildFeaturedBuyerCards(featured) {
  if (!Array.isArray(featured)) return [];
  return featured.map((m, idx) => {
    const id = String(m.id || `B${idx}`).replace(/^#?B?/, "");
    const story = m.story || null;
    const signals = (m.intentSignals || []).map((label) => {
      const strong = /cash|pre-approved|agreement/i.test(label);
      return { label, strong };
    });
    return {
      id,
      score: safeNum(m.score),
      topTag: idx === 0,
      story,
      signals,
      budgetDisplay: formatBudgetRangeClean(m.budgetMin, m.budgetMax),
      timelineDisplay: m.timeline || "Flexible",
      musts: Array.isArray(m.topNiceToHaves) ? m.topNiceToHaves : [],
      why: m.whyMatches || "",
      brokerage: m.brokerage || ""
    };
  });
}

function buildAdditionalBuyerRows(additional) {
  if (!Array.isArray(additional)) return [];
  return additional.map((m, idx) => {
    const id = String(m.id || `B${idx}`).replace(/^#?B?/, "");
    const financeLabel = (m.financeType || "Conv").replace(/^Conventional$/i, "Conv");
    return {
      id,
      score: safeNum(m.score),
      budget: formatBudgetRangeClean(m.budgetMin, m.budgetMax),
      financeLabel,
      timing: m.timeline || "Flexible",
      musts: Array.isArray(m.topNiceToHaves) && m.topNiceToHaves.length ? m.topNiceToHaves.join(" - ") : "-",
      brokerage: m.brokerage || ""
    };
  });
}

export function buildCompositionTiles(apiData) {
  const mp = apiData.matchPriorities || {};
  const bd = apiData.budgetDistribution || {};
  const fm = apiData.financeMix || {};
  const td = apiData.timelineDistribution || {};
  const matches = apiData.matches || {};
  const featured = Array.isArray(matches.featured) ? matches.featured : [];

  const cashShare = safeNum(fm.Cash);
  const conventional = safeNum(fm.Conventional);
  const preApproved = cashShare + conventional;
  const readyUnder60 = safeNum(td.ready_now) + safeNum(td["30_days"]);
  const top = mp.topNiceToHaves && mp.topNiceToHaves[0];
  const topPct = top ? safeNum(top.percent) : 0;
  const topName = top ? top.name : "-";
  const total = safeNum(matches.total);
  const featuredCount = featured.length;
  const cashCount = Math.round((cashShare / 100) * featuredCount);
  const topCount = Math.round((topPct / 100) * total);

  return [
    {
      eyebrow: "Median match budget",
      valueDisplay: `$${formatK(bd.median)}`,
      accent: false,
      wordy: false,
      sub: `${total ? `${total} matched buyers` : "No matches"}; sweet spot $${formatK(bd.sweetSpotMin)}-$${formatK(bd.sweetSpotMax)}.`
    },
    {
      eyebrow: "Paying cash",
      valueDisplay: `${cashShare}%`,
      accent: true,
      wordy: false,
      sub: `${cashCount} cash buyer${cashCount === 1 ? "" : "s"} of ${featuredCount} featured matches.`
    },
    {
      eyebrow: "Pre-approved",
      valueDisplay: `${preApproved}%`,
      accent: false,
      wordy: false,
      sub: `Cash (${cashShare}%) plus financed-with-letter (${conventional}%).`
    },
    {
      eyebrow: "Ready < 60 days",
      valueDisplay: `${readyUnder60}%`,
      accent: false,
      wordy: false,
      sub: `${safeNum(td.ready_now)}% can close in 30 days, ${safeNum(td["30_days"])}% in 30-60 days.`
    },
    {
      eyebrow: "Top nice-to-have",
      valueDisplay: topName,
      accent: false,
      wordy: true,
      sub: top
        ? `Named by ${topPct}% of matches - ${topCount} of ${total} Want-Listings flag it as a preferred feature.`
        : "No nice-to-have data available for this match pool."
    }
  ];
}

export function buildZipTiles(apiData) {
  const zc = apiData.zipContext || {};
  const yz = zc.yourZip || {};
  const fm = apiData.financeMix || {};
  const property = apiData.property || {};
  const ask = safeNum(property.listPrice);
  const median = safeNum(yz.medianBudget);
  const delta = ask - median;
  const top = (apiData.matchPriorities?.topNiceToHaves || [])[0];

  return [
    {
      eyebrow: `Median ${property.zip} budget`,
      valueDisplay: `$${formatK(median)}`,
      accent: false,
      wordy: false,
      sub: delta > 0
        ? `$${formatK(Math.abs(delta))} below your $${formatK(ask)} ask.`
        : `$${formatK(Math.abs(delta))} above your $${formatK(ask)} ask.`
    },
    {
      eyebrow: `Most-wanted in ${property.zip}`,
      valueDisplay: top ? top.name : "-",
      accent: false,
      wordy: true,
      sub: top
        ? `Named by ${safeNum(top.percent)}% of your matches.`
        : "No top feature data."
    },
    {
      eyebrow: "Cash share",
      valueDisplay: `${safeNum(fm.Cash)}% pool`,
      accent: true,
      wordy: true,
      sub: `Your match pool is ${safeNum(fm.Cash)}% cash plus ${safeNum(fm.Conventional)}% pre-approved conventional.`
    }
  ];
}

function buildAdjacentComparison(apiData) {
  const zc = apiData.zipContext || {};
  const yz = zc.yourZip || {};
  const yourZip = apiData.property?.zip || yz.code;
  const adj = Array.isArray(zc.adjacentZips) ? zc.adjacentZips : [];

  const adjAvgCash = adj.length
    ? Math.round(adj.reduce((s, a) => s + safeNum(a.perfectFitShare), 0) / adj.length)
    : 0;

  const yourRow = {
    zipLabel: `${yourZip} - your zip`,
    you: true,
    medianBudget: `$${formatK(yz.medianBudget)}`,
    cashShare: `${safeNum(yz.perfectFitShare)}%`,
    read: `${safeNum(yz.buyers)} active Want-Listings in your zip.`
  };

  const adjRows = adj.map((a) => ({
    zipLabel: `${a.code} - adjacent ${a.direction}`,
    you: false,
    medianBudget: `$${formatK(a.medianBudget)}`,
    cashShare: `${safeNum(a.perfectFitShare)}%`,
    read: a.insight || `${safeNum(a.buyers)} active Want-Listings.`
  }));

  return [yourRow, ...adjRows];
}

function buildVelocity(apiData) {
  const zc = apiData.zipContext || {};
  const dv = zc.demandVelocity || {};
  const buckets = Array.isArray(dv.weeklyBuckets) ? dv.weeklyBuckets : [0, 0, 0, 0];
  const max = Math.max(1, ...buckets);
  const labels = ["3 wks ago", "2 wks ago", "Last wk", "This wk"];
  const weeks = buckets.map((count, idx) => ({
    label: labels[idx] || `Wk ${idx + 1}`,
    count: safeNum(count),
    height: Math.max(6, Math.round((safeNum(count) / max) * 45)),
    accent: idx >= 2
  }));
  const change = safeNum(dv.changePercent);
  const sign = change > 0 ? "+" : "";
  return {
    weeks,
    caption: `${dv.currentPeriod || 0} new Want-Listings in the last 30 days, vs ${dv.priorPeriod || 0} in the prior 30 - **a ${sign}${change}% lift**.`,
    trendLabel: "30-day trend",
    trendValueDisplay: `${sign}${change}%`,
    trendBody: `${dv.currentPeriod || 0} new Want-Listings in ${apiData.property?.zip || "this zip"} over the last 30 days vs ${dv.priorPeriod || 0} in the prior 30.`
  };
}

export function computeAlignmentScore(apiData) {
  const featured = apiData.matches?.featured || [];
  if (!featured.length) {
    const mp = apiData.matchPriorities || {};
    const total = mp.totalRanked || 0;
    const met = mp.propertyMet || 0;
    return total ? Math.round((met / total) * 100) : 0;
  }
  const sum = featured.reduce((s, m) => s + safeNum(m.score), 0);
  return Math.round(sum / featured.length);
}

export function buildScoreDistribution(matches) {
  if (!matches) return [];
  const all = [
    ...(matches.featured || []),
    ...(matches.additional || [])
  ];
  const buckets = {
    "90-100 - strongest": { count: 0, accent: true },
    "80-89 - strong": { count: 0, tone: "ink" },
    "70-79 - solid": { count: 0, tone: "mid" },
    "60-69 - partial": { count: 0, tone: "dim" }
  };
  all.forEach((m) => {
    const s = safeNum(m.score);
    if (s >= 90) buckets["90-100 - strongest"].count += 1;
    else if (s >= 80) buckets["80-89 - strong"].count += 1;
    else if (s >= 70) buckets["70-79 - solid"].count += 1;
    else if (s >= 60) buckets["60-69 - partial"].count += 1;
  });
  return Object.entries(buckets).map(([label, props]) => ({ label, ...props }));
}

function buildZipBottomLine(apiData) {
  const property = apiData.property || {};
  const zc = apiData.zipContext || {};
  const yz = zc.yourZip || {};
  const dv = zc.demandVelocity || {};
  const change = safeNum(dv.changePercent);
  const sign = change > 0 ? "up" : "down";
  return `${property.zip} has **${safeNum(yz.buyers)} active matches** with median budget $${formatK(yz.medianBudget)}. New Want-Listings are ${sign} ${Math.abs(change)}% over the prior 30 days.`;
}

function buildPrioritiesBottomLine(apiData) {
  const mp = apiData.matchPriorities || {};
  const top = mp.topNiceToHaves && mp.topNiceToHaves[0];
  const bd = apiData.budgetDistribution || {};
  const td = apiData.timelineDistribution || {};
  const fm = apiData.financeMix || {};
  if (!top) {
    return `Match pool shows budget centered at $${formatK(bd.median)}, with ${safeNum(td["30_days"])}% targeting a 30-60 day close.`;
  }
  return `The most-wanted feature among your matches is **${top.name.toLowerCase()} (${safeNum(top.percent)}% of matches)**. Most matches cluster between $${formatK(bd.sweetSpotMin)}-$${formatK(bd.sweetSpotMax)}, your $${formatK(bd.askPrice)} ask sits inside that band. ${safeNum(td["30_days"])}% of matches are timed for a 30-60 day close, ${safeNum(fm.Cash)}% are paying cash.`;
}

function buildPoolBottomLine(apiData) {
  const matches = apiData.matches || {};
  const featured = matches.featured || [];
  const fm = apiData.financeMix || {};
  const top = featured[0];
  if (!top) return "No matches yet for this property.";
  const cashShare = safeNum(fm.Cash);
  const preApproved = cashShare + safeNum(fm.Conventional);
  return `Your top match scored **${safeNum(top.score)}%**. Of all ${safeNum(matches.total)} matches, ${preApproved}% are pre-approved or paying cash.`;
}

function buildAlignmentBottomLine(apiData) {
  const mp = apiData.matchPriorities || {};
  const met = safeNum(mp.propertyMet);
  const total = safeNum(mp.totalRanked);
  const gapList = (mp.topNiceToHaves || [])
    .filter((t) => !t.propertyHas)
    .slice(0, 5)
    .map((t) => `${t.name.toLowerCase()} (${safeNum(t.percent)}%)`);
  const gapStr = gapList.length ? `Gaps: ${gapList.join(", ")}.` : "";
  return `Your property has **${met} of the ${total} most-wanted features** among matched buyers. ${gapStr}`.trim();
}

function buildHeroStats(apiData) {
  const bd = apiData.budgetDistribution || {};
  const mp = apiData.matchPriorities || {};
  const top = (mp.topNiceToHaves || [])[0];

  return [
    {
      kind: "budget",
      valueDisplay: bd.sweetSpotMin && bd.sweetSpotMax
        ? `$${formatK(bd.sweetSpotMin)}-${formatK(bd.sweetSpotMax)}`
        : `$${formatK(bd.median)}`,
      label: "Where buyers are looking to spend",
      accent: false,
      wordy: false
    },
    {
      kind: "feature",
      valueDisplay: top ? top.name : "-",
      label: "Most-wanted feature",
      accent: false,
      wordy: true
    },
    {
      kind: "alignment",
      valueDisplay: `${safeNum(mp.propertyMet)} of ${safeNum(mp.totalRanked)}`,
      label: "Most-wanted features your home has",
      accent: true,
      wordy: false
    }
  ];
}

function splitName(fullName) {
  if (!fullName) return { firstName: "", lastName: "" };
  const tokens = String(fullName).trim().split(/\s+/);
  if (tokens.length === 1) return { firstName: tokens[0], lastName: "" };
  return { firstName: tokens[0], lastName: tokens.slice(1).join(" ") };
}

function formatPhoneDisplay(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

function formatPhoneTel(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/[^\d+]/g, "");
  if (!digits) return "";
  if (digits.startsWith("+")) return digits;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return digits;
}

function adaptProperty(apiProperty) {
  if (!apiProperty) return {};
  const parsed = parseAddress(apiProperty.address);
  const street = parsed.street || apiProperty.address || "";
  const city = apiProperty.city || parsed.city;
  const state = apiProperty.state || parsed.state;
  const zip = apiProperty.zip || parsed.zip;
  const beds = safeNum(apiProperty.beds, null);
  const baths = safeNum(apiProperty.baths, null);
  const sqft = safeNum(apiProperty.sqft, null);
  const listPrice = safeNum(apiProperty.listPrice, 0);
  const bedsDisplay = beds != null ? `${beds} bd` : "";
  const bathsDisplay = baths != null ? `${baths} ba` : "";
  const sqftDisplay = sqft != null ? `${sqft.toLocaleString()} sqft` : "";
  const bedsBathsSqft = [bedsDisplay, bathsDisplay, sqftDisplay].filter(Boolean).join(" - ");

  return {
    addressLine1: street,
    city,
    state,
    zip,
    listedPrice: listPrice,
    listedPriceDisplay: formatPrice(listPrice),
    bedsBathsSqft,
    specs: {
      beds,
      baths,
      sqft,
      yearBuilt: safeNum(apiProperty.yearBuilt, null),
      lotAcres: apiProperty.lotAcres ?? null
    },
    photoUrl: apiProperty.photoUrl || null,
    inventoryId: apiProperty.inventoryId
  };
}

function adaptAgent(apiAgent, state) {
  if (!apiAgent) return {};
  const { firstName, lastName } = splitName(apiAgent.name);
  const phoneDisplay = formatPhoneDisplay(apiAgent.phone);
  const phoneTel = formatPhoneTel(apiAgent.phone);
  const licenseLabel = apiAgent.license || (state ? `${state} Lic #-` : "Licensed Real Estate Agent");
  const mlsLabel = apiAgent.mls ? `${apiAgent.mls} Member` : "Network Member";

  return {
    firstName,
    lastName,
    fullName: apiAgent.name || `${firstName} ${lastName}`.trim(),
    title: apiAgent.title || "Real Estate Advisor",
    email: apiAgent.email || "",
    phoneDisplay,
    phoneTel,
    brokerage: apiAgent.brokerage || "Independent Agent",
    licenseLabel,
    mlsLabel,
    photoUrl: apiAgent.photoUrl || null,
    userId: apiAgent.userId
  };
}

function defaultMethodologyBlocks(zip) {
  const z = zip || "this zip";
  return [
    {
      heading: "What we measure",
      body: `Every figure aggregates from active Want-Listings posted by licensed buyer agents to The Buyer Listing Service®. Two kinds of fields drive the report: **hard criteria** (must-haves like beds, baths, budget, location, finance type, timeline) and **preferences** (nice-to-haves like updated kitchen, pool, home office). Budget figures come from Budget_mn and Budget_mx. Timing comes from the buyer's stated TimeLine field, not from when the Want-Listing was posted. We do not estimate, model, or extrapolate - every figure is a SQL aggregation over real Want-Listings scoped to ${z}.`
    },
    {
      heading: "How matches are scored",
      body: "Our matching algorithm produces a 0-100 score for each Want-Listing against this property using location, budget overlap, bed and bath count, and nice-to-have feature overlap. Hard criteria determine whether a Want-Listing qualifies for the match pool at all; nice-to-haves contribute to where the score lands above that floor. The match pool reported here is filtered at a quality threshold and sorted descending by score."
    },
    {
      heading: "What we excluded",
      body: "Want-Listings whose buyer has gone under contract on another property, Want-Listings flagged as deleted or unpublished, and Want-Listings with no stated TimeLine. We do not exclude by post age - a Want-Listing posted six months ago with a 6-12 month TimeLine is still active demand. We do not include browser-only searches, saved searches, MLS portal traffic, or any signal that is not an agent-submitted Want-Listing."
    },
    {
      heading: "What this report does not claim",
      body: `We do not track days on market, listing strategies, sale prices, or contractor pricing - so this report contains no estimates of those. Adjacent-zip comparisons use the identical Want-Listing query scoped to each neighboring ZIP, not third-party MSA data. The "30-day trend" is a direct COUNT-by-week of new Want-Listings in ${z}.`
    }
  ];
}

function defaultSampleComposition() {
  return {
    heading: "Sample composition",
    paragraphs: [
      "Real-time buyers in this report are buyer Want-Listings posted by licensed buyer agents that remain active and aligned with this property's timing window. The relevance signal is the buyer's stated TimeLine field, not how recently their agent posted - we filter on intent, not freshness.",
      "This is the most engaged, intent-explicit segment of the active buyer pool. Browsing-only buyers (passive search activity on consumer portals) are not included. The Buyer Listing Service® intentionally captures only buyers whose agent has filed structured criteria on their behalf.",
      "Treat distributions, percentages, and patterns as a **high-intent sample** - directionally accurate for understanding what serious buyers want, even if the underlying market is larger."
    ]
  };
}

function defaultDisclaimer(zip) {
  const z = zip || "the subject zip";
  return `This report reflects buyer demand specifically in zip code ${z} as of the generation date. It does not constitute an appraisal, is not USPAP-compliant, and does not represent a valuation of the subject property. Pricing decisions should be made in consultation with your licensed agent and other market analyses. Demand signals are derived from active agent-submitted Want-Listings within the network at the time of generation. Data is believed reliable but not guaranteed. The Buyer Listing Service® is a registered trademark. All rights reserved.`;
}

export function adaptApiResponse(apiData) {
  if (!apiData) return apiData;
  if (apiData.hero) return apiData;

  const property = adaptProperty(apiData.property);
  const agent = adaptAgent(apiData.agent, property.state);
  const matchesApi = apiData.matches || {};
  const featuredCount = (matchesApi.featured || []).length;
  const totalMatches = safeNum(matchesApi.total);

  const featured = buildFeaturedBuyerCards(matchesApi.featured);
  const additional = buildAdditionalBuyerRows(matchesApi.additional);
  const matchesAdapted = {
    mode: matchesApi.mode || "balanced",
    totalMatches,
    featuredCount,
    dashboardOverflow: totalMatches - (featured.length + additional.length) > 0
      ? totalMatches - (featured.length + additional.length)
      : 0
  };

  const ranked = buildPrioritiesRanked(apiData.matchPriorities);
  const budgetChart = buildBudgetChart(apiData.budgetDistribution, property.listedPrice);
  const timing = buildTimingForView(apiData.timelineDistribution);
  const finance = buildFinanceForView(apiData.financeMix);

  const meets = (apiData.matchPriorities?.topNiceToHaves || [])
    .filter((t) => t.propertyHas)
    .map((t) => ({ label: t.name, pct: safeNum(t.percent) }));
  const gaps = (apiData.matchPriorities?.topNiceToHaves || [])
    .filter((t) => !t.propertyHas)
    .map((t) => ({ label: t.name, pct: safeNum(t.percent) }));

  const distribution = buildScoreDistribution(matchesApi);
  const fitScore = computeAlignmentScore(apiData);
  const propertyMet = safeNum(apiData.matchPriorities?.propertyMet);
  const totalRanked = safeNum(apiData.matchPriorities?.totalRanked);

  const additionalCount = additional.length;
  const dashOverflow = matchesAdapted.dashboardOverflow;
  const additionalCaption = dashOverflow
    ? `${additionalCount} additional matches - ${dashOverflow} more in your dashboard`
    : `${additionalCount} additional matches`;

  const slug = slugify(apiData.property?.address || `inventory-${apiData.property?.inventoryId || "report"}`);

  return {
    meta: {
      generatedAt: apiData.generatedAt,
      generatedAtDisplay: formatDateTime(apiData.generatedAt, property.state),
      propertyId: apiData.property?.inventoryId,
      reportSlug: slug,
      downloadFilename: `active-buyer-report-${slug}.pdf`
    },
    property,
    agent,
    matches: matchesAdapted,
    hero: {
      stats: buildHeroStats(apiData)
    },
    priorities: {
      title: "What your matches are looking for.",
      lede: `Preferences, budgets, and timing of buyers actively searching for properties like yours in ${property.zip || "this market"}.`,
      ranked,
      budget: budgetChart,
      timing,
      finance,
      bottomLine: buildPrioritiesBottomLine(apiData)
    },
    pool: {
      title: "Your match pool.",
      lede: `${totalMatches} anonymized buyer${totalMatches === 1 ? "" : "s"} with real criteria, motivation, and intent signals - not aggregated patterns.`,
      featured,
      additional,
      additionalCaption,
      tiles: buildCompositionTiles(apiData),
      bottomLine: buildPoolBottomLine(apiData)
    },
    alignment: {
      title: "How your property aligns with what buyers want.",
      lede: "Match scores reflect both must-haves (beds, baths, budget, location) and nice-to-haves (features and preferences). Must-haves are already baked into the match pool itself - if a buyer is matched, your property qualifies. This section shows the nice-to-have alignment: which preferred features your property has, and where the gaps are.",
      fit: {
        score: fitScore,
        out: 100,
        summaryLabel: `Property Alignment - ${propertyMet} of ${totalRanked} top nice-to-haves met`,
        headline: meets.length || gaps.length
          ? `Hits ${meets.length} of ${meets.length + gaps.length} top preferences.`
          : "No nice-to-have alignment data available yet.",
        sub: "A single 0-100 score blending location, budget, bed/bath, and nice-to-have feature overlap against your matched buyers."
      },
      meets,
      gaps,
      distribution,
      distributionCaption: distribution.length
        ? `${distribution.reduce((s, d) => s + (d.count || 0), 0)} matches scored.`
        : "No score distribution available.",
      bottomLine: buildAlignmentBottomLine(apiData)
    },
    agentNote: {
      defaultText: apiData.agentNote?.note || apiData.agentNote?.defaultText || "",
      placeholderText: "[Your note will appear here. Use this space to explain your pricing rationale, highlight specific buyer signals you're tracking, and outline your strategy for getting the strongest offer. Sellers only see this once you've filled it in.]",
      savedTimestamp: apiData.agentNote?.updatedAt || apiData.agentNote?.savedTimestamp || ""
    },
    zipOverview: {
      title: `${property.zip || "Local"} market overview.`,
      lede: `How the wider engaged buyer pool in ${property.zip || "this market"} compares to your matches - useful for a read on neighborhood momentum and where your matches sit inside it.`,
      tiles: buildZipTiles(apiData),
      comparison: buildAdjacentComparison(apiData),
      velocity: buildVelocity(apiData),
      bottomLine: buildZipBottomLine(apiData)
    },
    methodology: {
      title: "How we counted, what we excluded.",
      lede: "A short note on the data behind this report so you can read every number with confidence.",
      blocks: defaultMethodologyBlocks(property.zip),
      sampleComposition: defaultSampleComposition(),
      disclaimer: defaultDisclaimer(property.zip)
    }
  };
}

export default adaptApiResponse;
